// src/features/variant_workflow/clinical_observation/VariantDatabasesSection.tsx
/**
 * VariantDatabasesSection Component
 *
 * This component displays variant database information including ClinVar records and assertions.
 * It provides functionality to view and update ClinVar data for a specific variant.
 */
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, notification, TableProps } from 'antd';
import DOMPurify from 'dompurify';
import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import Modal from '../../../foundation/components/modal/Modal';
import ResizableTable from '../../../foundation/components/resizable_table/ResizableTable';
import VersionHistoryFooter from '../../../foundation/components/version_history_footer/VersionHistoryFooter';
import { LINK_BLUE } from '../../../foundation/config/colors';
import { boldColValueRenderer } from '../../../foundation/utils/boldColValueRenderer';
import {
  useFetchUpdatedClinvarRecordMutation,
  useGetVariantClinvarAssertionsQuery,
  useGetVariantClinvarRecordQuery,
  useGetVariantDatabasesQuery,
  useGetVariantLinkoutsQuery,
} from '../../api/variantApi';
import CollapseHeaderForSections from '../components/CollapseHeaderForSections';
import CollapsePanel from '../components/CollapsePanel';
import LoadMoreBtn from '../components/LoadMoreBtn';
import { NOTIFICATION_MESSAGES, VARIANT_STAGE_TYPES } from '../constants';
import { useVariantStep } from '../hooks/use_variant_step';
import { useVariantAccess } from '../hooks/useVariantAccess';
import {
  ClinvarAssertion,
  ClinvarRecordType,
  ClinVarVariationChoice,
  HaplotypeInfo,
  VariantDatabase,
} from '../redux';
import VariantDatabaseModal from './VariantDatabaseModal';

const VariantDatabasesSection = () => {
  /**
   * Get variant ID from URL parameters
   */
  const params = useParams();

  const [fetchUpdatedClinvarRecordMutation] =
    useFetchUpdatedClinvarRecordMutation();

  const [isDatabaseModalVisible, setIsDatabaseModalVisible] = useState(false);
  const [selectedDatabase, setSelectedDatabase] =
    useState<VariantDatabase | null>(null);

  // State for ambiguity handling
  const [ambiguousChoices, setAmbiguousChoices] = useState<
    ClinVarVariationChoice[]
  >([]);
  const [isAmbiguityModalVisible, setIsAmbiguityModalVisible] = useState(false);
  const [selectedEsearchId, setSelectedEsearchId] = useState<string | null>(
    null,
  );

  // State for controlling the visibility of additional ClinVar assertions
  const [showMore, setShowMore] = useState(false);
  // State for tracking ClinVar data update loading status
  const [isClinvarLoading, setIsClinvarLoading] = useState(false);

  // Read ClinVar data straight from the query hook's cache rather than a selector
  const { data: clinvarDataSource } = useGetVariantClinvarRecordQuery(
    { variant_id: params.vid },
    { skip: !params.vid },
  );
  const { data: clinvarAssertions } = useGetVariantClinvarAssertionsQuery(
    { variant_id: params.vid },
    { skip: !params.vid },
  );
  const { data: dataSource } = useGetVariantDatabasesQuery(
    { variant_id: params.vid },
    { skip: !params.vid },
  );

  // The backend returns a single ClinvarRecordType object; wrap into an array
  // only here where Ant Design's Table requires an array dataSource
  // (analogous to molecularImpactHeader in MolecularImpactSection).
  const clinvarRecords = clinvarDataSource ? [clinvarDataSource] : [];

  const { canEditWorkflow } = useVariantAccess();

  const handleEditDatabase = useCallback((database: VariantDatabase) => {
    // Allow opening the modal for viewing even if the user is not logged in.
    setSelectedDatabase(database);
    setIsDatabaseModalVisible(true);
  }, []);

  // Read linkouts straight from the query hook's cache rather than a selector
  const { data: linkouts } = useGetVariantLinkoutsQuery(
    { variant_id: params.vid },
    { skip: !params.vid },
  );
  const stageInfo = useVariantStep(VARIANT_STAGE_TYPES.CLINICAL_OBSERVATION);

  // Table columns configuration for variant databases
  const columns: TableProps<VariantDatabase>['columns'] = useMemo(
    () => [
      {
        title: 'Database',
        dataIndex: 'database',
        key: 'database',
        render: (text, record) => (
          <span
            className="clinical-obs__report-category-link"
            onClick={() => handleEditDatabase(record)}
          >
            {text}
          </span>
        ),
      },
      {
        title: 'Classification',
        dataIndex: 'classification',
        key: 'classification',
      },
      {
        title: 'Associated with phenotype',
        dataIndex: 'associated_phenotype',
        key: 'associated_phenotype',
      },
      {
        title: 'Evidence',
        dataIndex: 'evidence',
        key: 'evidence',
      },
      {
        title: 'Comment',
        dataIndex: 'comment',
        key: 'comment',
        render: (text: string | null | undefined) =>
          text ? (
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(text),
              }}
            />
          ) : null,
      },
    ],
    [handleEditDatabase],
  );

  // DNAWZ-506: Check once whether this variant belongs to any haplotypes so we can
  // conditionally show or hide the "Haplotype" column — no empty column when there's nothing to show.
  const hasHaplotypes = (clinvarRecords[0]?.haplotypes ?? []).length > 0;

  // Table columns configuration for ClinVar records
  const clinvarColumns: TableProps<ClinvarRecordType>['columns'] = [
    {
      title: 'Interpretation',
      dataIndex: 'interpretation',
      key: 'interpretation',
      render: boldColValueRenderer,
    },
    {
      title: 'Description*',
      dataIndex: 'description',
      key: 'description',
      render: boldColValueRenderer,
    },
    {
      title: 'Review status',
      dataIndex: 'review_status',
      key: 'review_status',
      render: boldColValueRenderer,
    },
    {
      title: 'Submissions',
      dataIndex: 'submissions',
      key: 'submissions',
      render: boldColValueRenderer,
    },
    {
      title: 'Last submission',
      dataIndex: 'recent_submission_date',
      key: 'recent_submission_date',
      render: boldColValueRenderer,
    },
    {
      title: 'Variation ID',
      dataIndex: 'variation_id',
      key: 'variation_id',
      render: (text: string, record: any) => (
        <a
          href={record.variation_id_url}
          target="_blank"
          rel="noopener noreferrer"
          className="interpretation__intervar-link__anchor"
        >
          {text}
        </a>
      ),
    },
    // DNAWZ-506: Only inject the "Haplotype" column when the variant actually has haplotype data.
    // Each haplotype is a clickable ClinVar link
    ...(hasHaplotypes
      ? [
          {
            title: 'Haplotype',
            dataIndex: 'haplotypes',
            key: 'haplotypes',
            render: (haplotypes: HaplotypeInfo[]) => (
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {haplotypes?.map((h) => (
                  <li key={h.variation_id}>
                    <a
                      href={h.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: LINK_BLUE }}
                    >
                      {h.variation_name}
                    </a>
                  </li>
                ))}
              </ul>
            ),
          },
        ]
      : []),
  ];

  // Toggle visibility of additional ClinVar assertions
  const handleShowMoreClick = () => {
    setShowMore((v) => !v);
  };

  // Table columns configuration for ClinVar assertions
  const clinvarInterColumns: TableProps<ClinvarAssertion>['columns'] = [
    {
      title: 'Interpretation',
      dataIndex: 'interpretation',
      key: 'interpretation',
      render: (text) => (
        <span>
          <b>{text}</b>
        </span>
      ),
    },
    {
      title: 'Review status',
      dataIndex: 'review_status',
      key: 'review_status',
    },
    {
      title: 'Submitter',
      dataIndex: 'submitter',
      key: 'submitter',
    },
    {
      title: 'Condition',
      dataIndex: 'condition',
      key: 'condition',
    },
    {
      title: 'Evidence',
      dataIndex: 'evidence',
      key: 'evidence',
    },
  ];

  /**
   * Handles updating ClinVar data for the current variant
   * Fetches the latest ClinVar record and updates the Redux store
   */
  const handleUpdateDataClick = async (forceClinVarId?: string) => {
    // Prevent multiple simultaneous updates
    if (isClinvarLoading) {
      return;
    }
    try {
      if (params.vid) {
        setIsClinvarLoading(true);
        // Trigger the mutation and await the unwrapped promise
        const data = await fetchUpdatedClinvarRecordMutation({
          variant_id: params.vid,
          force_clinvar_id: forceClinVarId,
        }).unwrap();

        // invalidatesTags marks the ClinVar query stale, so it refetches automatically
        if (data && 'status' in data && data.status === 'AMBIGUOUS') {
          setAmbiguousChoices(data.choices);
          setIsAmbiguityModalVisible(true);
        } else {
          setIsAmbiguityModalVisible(false);
          setAmbiguousChoices([]);
          notification.success({
            message: NOTIFICATION_MESSAGES.SUCCESS,
            description: NOTIFICATION_MESSAGES.CLINVAR_RECORD_UPDATED,
          });
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsClinvarLoading(false);
    }
  };

  const handleAddDatabaseClick = () => {
    // Only opens when enabled; button is disabled for unauthorized users.
    setSelectedDatabase(null);
    setIsDatabaseModalVisible(true);
  };

  const handleDatabaseModalClose = () => {
    setSelectedDatabase(null); // Clear selected database when modal is closed
    setIsDatabaseModalVisible(false);
  };

  const handleAmbiguityModalClose = () => {
    setIsAmbiguityModalVisible(false);
    setAmbiguousChoices([]);
    setSelectedEsearchId(null);
  };

  const ambiguityColumns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Variation ID',
      dataIndex: 'variation_id',
      key: 'variation_id',
      render: (text: string, record: ClinVarVariationChoice) => (
        <a
          href={record.variation_id_url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {text}
        </a>
      ),
    },
  ];

  const lsdbUrl = linkouts?.lsdb_url;
  const lovdUrl_hg19 = linkouts?.lovd_url_hg19;
  const lovdUrl_hg38 = linkouts?.lovd_url_hg38;
  const clingenErepoUrl = linkouts?.clingen_erepo_url;

  // disable "add database" button if user is not logged in, institute not allowed, stage is not editable, or variant is locked
  const isDatabaseDisabled =
    !canEditWorkflow ||
    (stageInfo?.status !== 'revised' && stageInfo?.status !== 'not_started');
  // disable "update data" link in VersionHistoryFooter if user is not logged in, institute not allowed, stage is not editable, or variant is locked
  const isUpdateDisabled =
    !canEditWorkflow ||
    (stageInfo?.status !== 'revised' && stageInfo?.status !== 'not_started');

  return (
    <>
      {isDatabaseModalVisible && (
        <VariantDatabaseModal
          onClose={handleDatabaseModalClose}
          variant_id={params.vid}
          existingDatabase={selectedDatabase}
        />
      )}
      <Modal
        open={isAmbiguityModalVisible}
        title="Multiple ClinVar records found"
        onCancel={handleAmbiguityModalClose}
        centered={true}
        draggable={true}
        maskClosable={false}
        footer={
          <div className="c-form__btn-wrapper h-proband-modal__form-btn-wrapper">
            <Button
              type="primary"
              onClick={() =>
                handleUpdateDataClick(selectedEsearchId || undefined)
              }
              disabled={
                !selectedEsearchId || isClinvarLoading || isUpdateDisabled
              }
            >
              Save changes
            </Button>
          </div>
        }
      >
        <p>
          ClinVar query returned multiple possible records. Please select the
          correct one:
        </p>
        <ResizableTable
          dataSource={ambiguousChoices}
          columns={ambiguityColumns}
          pagination={false}
          rowKey="esearch_id"
          loading={isClinvarLoading}
          persistenceKey="clinvar-ambiguity"
          rowSelection={{
            type: 'radio',
            selectedRowKeys: selectedEsearchId ? [selectedEsearchId] : [],
            onSelect: (record) => setSelectedEsearchId(record.esearch_id),
          }}
          onRow={(record) => ({
            onClick: () => {
              if (!isClinvarLoading) {
                setSelectedEsearchId(record.esearch_id);
              }
            },
            style: { cursor: 'pointer' },
          })}
        />
      </Modal>
      <CollapsePanel
        title={<CollapseHeaderForSections title="Variant databases" />}
        panelKey="variant-databases"
        defaultActiveKey="variant-databases"
      >
        <div className="interpretation__intervar-link">
          {lsdbUrl && (
            <a
              href={lsdbUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="interpretation__intervar-link__anchor"
            >
              LSDBs
            </a>
          )}
          <span
            className="interpretation__intervar-link__anchor"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              if (lovdUrl_hg19) {
                window.open(lovdUrl_hg19, '_blank', 'noopener,noreferrer');
              }
              if (lovdUrl_hg38) {
                window.open(lovdUrl_hg38, '_blank', 'noopener,noreferrer');
              }
            }}
          >
            LOVD
          </span>
          {clingenErepoUrl && (
            <a
              href={clingenErepoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="interpretation__intervar-link__anchor"
            >
              ERepo
            </a>
          )}
        </div>
        <Button
          className="clinical-obs__variant-databases-add-btn"
          type="primary"
          onClick={handleAddDatabaseClick}
          icon={<FontAwesomeIcon icon={faPlus} />}
          disabled={isDatabaseDisabled}
        >
          Database
        </Button>
        <div className="clinical-obs__variant-databases">
          <ResizableTable
            dataSource={dataSource ?? []}
            columns={columns}
            pagination={false}
            className="clinical-obs__variant-database-table"
            persistenceKey="variant-databases"
          />
          {/* DNAWZ-506: Show the HGVS nomenclature right after the title when available,
              e.g. "ClinVar record for NM_000402.4(G6PD):c.292G>A (p.Val98Met)" */}
          <div className="clinical-obs__clinvar-header">
            ClinVar record
            {clinvarRecords[0]?.variation_name
              ? ` for ${clinvarRecords[0].variation_name}`
              : ''}
          </div>
          {clinvarRecords[0]?.variation_id === null ? (
            <>
              <div>{clinvarRecords[0]?.last_import}</div>
              <VersionHistoryFooter
                historyInfo={clinvarRecords[0]?.source_version}
                onUpdateClick={() =>
                  handleUpdateDataClick(
                    clinvarRecords[0]?.variation_id ?? undefined,
                  )
                }
                disabled={isUpdateDisabled}
              />
            </>
          ) : clinvarRecords.length > 0 ? (
            <>
              <div className="clinical-obs__clinvar-records-wrapper">
                <ResizableTable
                  columns={clinvarColumns}
                  pagination={false}
                  dataSource={clinvarRecords}
                  rowKey={(record) => record.variation_id}
                  loading={isClinvarLoading}
                  persistenceKey="clinvar-record"
                />
                <VersionHistoryFooter
                  historyInfo={clinvarRecords[0]?.source_version}
                  onUpdateClick={() =>
                    handleUpdateDataClick(
                      clinvarRecords[0]?.variation_id ?? undefined,
                    )
                  }
                  disabled={isUpdateDisabled}
                />
              </div>
              {showMore && (
                <div>
                  <ResizableTable
                    columns={clinvarInterColumns}
                    dataSource={clinvarAssertions ?? []}
                    pagination={false}
                    persistenceKey="clinvar-assertions"
                  />
                </div>
              )}
              <LoadMoreBtn
                showMore={showMore}
                handleShowMoreClick={handleShowMoreClick}
              />
            </>
          ) : null}
        </div>
      </CollapsePanel>
    </>
  );
};

export default VariantDatabasesSection;
